/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2022, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { Logger, MessageService } from '@core/lib/services';
import { first } from 'rxjs/operators';
import { NotifyService } from '@app/common/notify.service';
import { TranslateService } from '@ngx-translate/core';
import { IntegrationStatusService } from '@app/di/integration-status.service';

const log = new Logger('EnterpriseViewerStatusService');

@Injectable({
  providedIn: 'root'
})
export class EnterpriseViewerStatusService {
  private static readonly INTEGRATION_STATUS_EV = 'ev';

  // will emit true if the listeners in the system status component are initialized,
  // before that we should not send any status information
  private _systemStatusListening: Subject<boolean> = new ReplaySubject<boolean>(1);
  public systemStatusListening$: Observable<boolean> = this._systemStatusListening.asObservable();

  // will emit exactly one true or false value
  // true - fhircast websocket connection initialized and the viewer is started
  // false - we should fallback to use UV
  private _useViewerWithFhircast: Subject<boolean> = new ReplaySubject<boolean>(1);
  public useViewerWithFhircast$: Observable<boolean> = this._useViewerWithFhircast.asObservable().pipe(first());

  private launchedExamId: number;

  private _logoutInProgress = false;
  private _integrationStatus = IntegrationStatus.LOADING;
  private _firstInitializationSuccessful = false;

  constructor(
    private messageService: MessageService,
    private notifyService: NotifyService,
    private translate: TranslateService,
    private integrationStatusService: IntegrationStatusService
  ) {}

  public setLaunchedExamId(examId: number) {
    this.launchedExamId = examId;
  }

  public getLaunchedExamId() {
    return this.launchedExamId ? this.launchedExamId : 0;
  }

  public systemStatusListening() {
    this._systemStatusListening.next(true);
  }

  public setfirstInitializationSuccessful(status: boolean) {
    this._firstInitializationSuccessful = status;
  }

  public getIntegrationStatus() {
    return this._integrationStatus;
  }

  public isIntegrationReady() {
    switch (this._integrationStatus) {
      case IntegrationStatus.LAST_ACTION_FAILED:
      case IntegrationStatus.SYNC_ERROR:
      case IntegrationStatus.READY:
      case IntegrationStatus.IN_PROGRESS:
        return true;
      default:
        return false;
    }
  }

  public notifyViewerResponsePending() {
    return this.notifyService.critical(
      this.translate.instant('di.desktopIntegration.error.viewerResponsePendingOnTimeout'),
      this.translate.instant('di.dashboard.label.viewer')
    );
  }

  public isPendingRequest() {
    return this.integrationStatusService.isPendingRequest();
  }

  public viewerLoading() {
    this.changeIntegrationStatus(IntegrationStatus.LOADING, null);
  }

  public viewerInprogress() {
    this.changeIntegrationStatus(IntegrationStatus.IN_PROGRESS, null);
  }

  public readyIntegrationStatus() {
    this.changeIntegrationStatus(IntegrationStatus.READY, null);
  }

  public failedIntegrationStatus(integrationStatus: IntegrationStatus, message: string, isCritical: boolean) {
    this.changeIntegrationStatus(integrationStatus, message, isCritical);
  }

  public healthyIntegrationStatus() {
    this.changeIntegrationStatus(IntegrationStatus.HEALTHY, null);
  }

  private changeIntegrationStatus(integrationStatus: IntegrationStatus, message: string, isCritical = false) {
    this._integrationStatus = integrationStatus;
    this.integrationStatusService.setViewerHealthStatus(integrationStatus);
    switch (integrationStatus) {
      case IntegrationStatus.LOADING:
        this._useViewerWithFhircast.next(true);
        break;
      case IntegrationStatus.IN_PROGRESS:
        break;
      case IntegrationStatus.READY:
        this._firstInitializationSuccessful = true;
        break;
      case IntegrationStatus.HEALTHY:
        this.notifyService.success(this.translate.instant('di.desktopIntegration.titleViewerEnabled'));
        break;
      case IntegrationStatus.FAILED:
        this._useViewerWithFhircast.next(false);
        log.error('Fhircast integration status: ' + integrationStatus);
        this.notifyService[isCritical ? 'critical' : 'error'](
          this.translate.instant(message),
          this.translate.instant('di.desktopIntegration.titleViewerDisabled')
        );
        break;
      case IntegrationStatus.LAST_ACTION_FAILED:
      case IntegrationStatus.SYNC_ERROR:
        log.error('Fhircast integration status: ' + integrationStatus);
        this.notifyService[isCritical ? 'critical' : 'error'](
          this.translate.instant(message),
          this.translate.instant('di.dashboard.message.lastActionFailed')
        );
        break;
      default:
        log.error('Integration status change not handled.');
    }

    this.messageService.broadcast('integralStatusBoardUpdate', [
      EnterpriseViewerStatusService.INTEGRATION_STATUS_EV,
      integrationStatus
    ]);
  }

  get logoutInProgress(): boolean {
    return this._logoutInProgress;
  }

  set logoutInProgress(value: boolean) {
    this._logoutInProgress = value;
  }

  get firstInitializationSuccessful(): boolean {
    return this._firstInitializationSuccessful;
  }
}

export enum IntegrationStatus {
  LOADING = 'loading',
  IN_PROGRESS = 'pendingResponse',
  READY = 'ready',
  FAILED = 'failed',
  HEALTHY = 'healthy',
  LAST_ACTION_FAILED = 'lastActionFailed',
  SYNC_ERROR = 'syncError'
}
